import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsFooter, SmchsNav, SmchsStyleBlock } from '@/components/smchs/Shell';
import { CollectionStyle, GrainOverlay, Reveal } from '@/components/smchs/Collection';
import { ReplyComposer, ReplyList } from '@/components/smchs/Discussion';
import {
  apiErrorMessage,
  discussionKeys,
  timeAgo,
} from '@/components/smchs/discussion-lib';
import {
  bookclubClubsApi,
  bookclubDiscussionsApi,
  bookclubMembershipApi,
} from '@/services/bookclub-api';
import { useAuth } from '@/hooks/useAuth';
import { TokenManager } from '@/lib/token-manager';

/* =====================================================================
 * THREAD: one discussion, read in full.
 *
 * Reading is open (the API allows anonymous reads), so a visitor can
 * follow a conversation before deciding to join. Replying is club
 * members only, enforced server-side; this page mirrors that with a
 * join prompt for signed-in non-members and a sign-in prompt for
 * everyone else, rather than a reply box that fails on submit.
 * ===================================================================== */

const ThreadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const threadId = Number(id);

  return (
    <div className="smchs-home">
      <SmchsStyleBlock />
      <CollectionStyle />
      <SmchsNav />
      <main className="relative">
        {Number.isFinite(threadId) && threadId > 0 ? (
          <ThreadContent threadId={threadId} />
        ) : (
          <NotFoundBlock />
        )}
      </main>
      <SmchsFooter />
      <GrainOverlay />
    </div>
  );
};

const shell = 'max-w-[820px] mx-auto px-5 md:px-8';

const NotFoundBlock: React.FC = () => (
  <section className={`${shell} pt-16 md:pt-20 pb-24`}>
    <h1 className="font-display font-[900] text-[2rem] md:text-[2.6rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
      That thread is not here.
    </h1>
    <p className="mt-4 text-[15px] leading-[1.6] text-[var(--ink-soft)] max-w-[46ch]">
      It may have been removed by a librarian, or the link may be wrong.
    </p>
    <BackLink />
  </section>
);

const BackLink: React.FC = () => (
  <Link
    to="/book-clubs"
    className="mt-8 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
  >
    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} /> All book clubs
  </Link>
);

const ThreadContent: React.FC<{ threadId: number }> = ({ threadId }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const signedIn = TokenManager.isTokenValid();
  const [joining, setJoining] = useState(false);

  const {
    data: thread,
    isLoading,
    isError,
  } = useQuery({
    queryKey: discussionKeys.thread(threadId),
    queryFn: () => bookclubDiscussionsApi.getThread(threadId),
    staleTime: 30 * 1000,
    retry: false,
  });

  /* Membership drives whether the composer renders. Anonymous callers get
     is_member: false from the same endpoint, so no auth gate is needed. */
  const { data: clubData } = useQuery({
    queryKey: ['bookclub', 'clubs', 'for-thread'],
    queryFn: () => bookclubClubsApi.getClubs({ page_size: 50 }),
    staleTime: 5 * 60 * 1000,
  });

  const club = clubData?.results.find((c) => c.slug === thread?.club_slug);

  const join = async () => {
    if (!club) return;
    setJoining(true);
    try {
      await bookclubMembershipApi.joinClub(club.slug);
      toast.success(`Joined ${club.name}`);
      qc.invalidateQueries({ queryKey: ['bookclub', 'clubs', 'for-thread'] });
      qc.invalidateQueries({ queryKey: ['student', 'desk', 'clubs'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not join this club'));
    } finally {
      setJoining(false);
    }
  };

  if (isLoading) {
    return (
      <section className={`${shell} pt-16 md:pt-20 pb-24`}>
        <div className="h-4 w-32 rounded bg-[var(--surface-2)] animate-pulse" />
        <div className="mt-6 h-10 w-3/4 rounded bg-[var(--surface-2)] animate-pulse" />
        <div className="mt-10 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (isError || !thread) return <NotFoundBlock />;

  return (
    <>
      <section className="border-b border-[var(--border)]">
        <div className={`${shell} pt-12 md:pt-16 pb-8 md:pb-10`}>
          <Link
            to={`/book-clubs/${thread.club_slug}`}
            className="inline-block hover:opacity-80 transition-opacity"
          >
            <Kicker label={club?.name ?? thread.club_slug} />
          </Link>
          <h1 className="mt-5 font-display font-[900] text-[2rem] sm:text-[2.5rem] md:text-[2.9rem] leading-[1.06] tracking-[-0.03em] text-[var(--ink)]">
            {thread.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
            <span>Started by {thread.author_name}</span>
            {club?.advisor_name && (
              <>
                <span aria-hidden className="w-px h-3 bg-[var(--border)]" />
                <span>Advised by {club.advisor_name}</span>
              </>
            )}
            <span aria-hidden className="w-px h-3 bg-[var(--border)]" />
            <span>{timeAgo(thread.created_at)}</span>
            <span aria-hidden className="w-px h-3 bg-[var(--border)]" />
            <span>
              {thread.reply_count} {thread.reply_count === 1 ? 'reply' : 'replies'}
            </span>
          </div>
        </div>
      </section>

      <section className={`${shell} pt-10 md:pt-12 pb-20 md:pb-24`}>
        <Reveal>
          <ReplyList
            threadId={threadId}
            replies={thread.replies}
            currentUserId={user?.id ?? null}
          />
        </Reveal>

        <div className="mt-10 pt-8 border-t border-[var(--border)]">
          {club?.is_member ? (
            <ReplyComposer threadId={threadId} placeholder="Add to the discussion" />
          ) : signedIn ? (
            <div className="rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6">
              <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[48ch]">
                Join {club?.name ?? 'this club'} to reply. Members set the reading and take
                part in every thread.
              </p>
              <button
                onClick={join}
                disabled={joining || !club}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all disabled:opacity-40"
              >
                {joining ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  `Join ${club?.name ?? 'this club'}`
                )}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6">
              <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[48ch]">
                Sign in with your SMCHS account to join this club and reply.
              </p>
              <Link
                to="/signin"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all"
              >
                Sign in
              </Link>
            </div>
          )}
        </div>

        <BackLink />
      </section>
    </>
  );
};

export default ThreadPage;
